import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './styles.css';
import './aws-config';
import { getUser, hasStoredTokens, logout } from './services/auth';
import { ThemeProvider } from './context/ThemeContext';
import useIdleTimeout from './hooks/useIdleTimeout';
import IdleWarningModal from './components/IdleWarningModal';

import Login from './pages/Login';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './pages/Dashboard';
import Contracts from './pages/Contracts';
import Companies from './pages/Companies';
import Workspace from './pages/Workspace';
import DocumentUpload from './pages/DocumentUpload';
import WorkspaceDetail from './pages/WorkspaceDetail';
import Insights from './pages/Insights';
import NewContract from './pages/NewContract';
import CustomerDetail from './pages/CustomerDetail';
import ForecastDashboard from './pages/ForecastDashboard';
import GrowthDashboard from './pages/GrowthDashboard';
import Users from './pages/Users';
import FinanceImport from './pages/FinanceImport';
import DeliveryDashboard from './pages/DeliveryDashboard';
import UteImport from './pages/UteImport';
import GrowthImport from './pages/GrowthImport';
import ClearanceTracking from './pages/ClearanceTracking';
import VisitRequests from './pages/VisitRequests';
import DeliveryConfig from './pages/DeliveryConfig';
import NdaTaGenerator from './pages/NdaTaGenerator';
import AgreementReview from './pages/AgreementReview';
import AgreementDetail from './pages/AgreementDetail';


const PAGE_TITLES = {
  dashboard: 'Executive Dashboard',
  workspace: 'Workspace',
  contracts: 'Contracts Dashboard',
  companies: 'Customers',
  'customer-detail': 'Customer Detail',
  insights: 'AI Insights',
  'new-contract': 'New Contract',
  'document-upload': 'Upload Document',
  'workspace-detail': 'Workspace Detail',
  users: 'User Management',
  settings: 'Settings',
  'forecast-dashboard': 'Forecast Dashboard',
  'forecast-tool': 'Forecast Tool',
  'reports-analysis': 'Reports & Analysis',
  'delivery-dashboard': 'Delivery Dashboard',
  'ute-reports': 'UTE Import',
  'delivery-config': 'Delivery Config',
  'profitability-reports': 'Profitability Reports',
  'growth-dashboard': 'Growth Dashboard',
  'growth-import': 'Growth Import',
  'nda-ta-generator': 'NDA/TA Generator',
  'agreement-review': 'Agreement Review',
  'agreement-detail': 'Agreement Detail',
  'security-dashboard': 'Security & Facilities Dashboard',
  'visit-requests': 'Visit Requests',
  'clearance-requests': 'Clearance Requests',
};

// Maps pages to the Cognito groups allowed access.
// Users not in any listed group (or admin) see "Access Denied" instead of "Coming Soon".
const PAGE_REQUIRED_GROUPS = {
  'security-dashboard': ['security'],
  'visit-requests': ['security'],
  'clearance-requests': ['security'],
  'delivery-dashboard': ['user', 'senior-leader', 'investor'],
  'ute-reports': ['user', 'senior-leader', 'investor'],
  'profitability-reports': ['user', 'senior-leader', 'investor'],
};

const COMING_SOON_PAGES = new Set([
  'workspace',
  'forecast-tool',
  'reports-analysis',
  'profitability-reports',
  'security-dashboard',
]);

function AppContent() {
  const [user, setUser] = useState(null);
  const [activePage, setActivePage] = useState(() => {
    return sessionStorage.getItem('bcore_activePage') || 'dashboard';
  });
  const [pageOptions, setPageOptions] = useState(() => {
    try {
      const stored = sessionStorage.getItem('bcore_pageOptions');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Quick localStorage check — if no tokens exist, skip getUser
        if (!hasStoredTokens()) {
          setAuthChecked(true);
          return;
        }

        const currentUser = await getUser();
        if (currentUser) {
          setUser(currentUser);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      }
      setAuthChecked(true);
    };

    initAuth();
  }, []);

  const handleLogout = useCallback(async () => {
    await logout();
    setUser(null);
    setActivePage('dashboard');
  }, []);

  // NIST 800-171 3.1.10 — 15-minute idle timeout
  const { showWarning, remainingSeconds, dismissWarning } = useIdleTimeout(handleLogout);

  const handleLoginSuccess = async () => {
    const currentUser = await getUser();
    if (currentUser) {
      setUser(currentUser);

      // SSO redirect-back: if the user arrived via ?redirect=<url> (e.g. from
      // Growth Tools Lambda@Edge), send them back after successful login.
      const params = new URLSearchParams(window.location.search);
      const redirectUrl = params.get('redirect');
      if (redirectUrl) {
        try {
          const url = new URL(redirectUrl);
          // Only redirect to trusted subdomains (same parent domain)
          const currentHost = window.location.hostname;
          const parentDomain = currentHost.split('.').slice(-2).join('.');
          if (url.hostname.endsWith(parentDomain)) {
            window.location.href = redirectUrl;
            return;
          }
        } catch { /* invalid URL — ignore and stay on portal */ }
      }
    }
  };

  if (!authChecked) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'var(--bcore-bg-primary)',
        color: 'var(--bcore-text-primary)',
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: '14px',
      }}>
        Authenticating...
      </div>
    );
  }

  if (!user) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  const isAdmin = user?.groups?.includes('admin') ?? false;

  const handleNavigate = (page, options = null) => {
    setActivePage(page);
    setPageOptions(options);
    sessionStorage.setItem('bcore_activePage', page);
    if (options) {
      sessionStorage.setItem('bcore_pageOptions', JSON.stringify(options));
    } else {
      sessionStorage.removeItem('bcore_pageOptions');
    }
  };

  const renderPage = () => {
    if (COMING_SOON_PAGES.has(activePage)) {
      const requiredGroups = PAGE_REQUIRED_GROUPS[activePage];
      const hasAccess = !requiredGroups
        || user?.groups?.includes('admin')
        || requiredGroups.some(g => user?.groups?.includes(g));

      return (
        <div style={{
          color: 'var(--bcore-text-dim)',
          textAlign: 'center',
          paddingTop: '60px',
          fontFamily: 'JetBrains Mono, monospace',
        }}>
          {hasAccess
            ? <>{PAGE_TITLES[activePage] || activePage} &mdash; Coming Soon</>
            : <>Access Denied &mdash; Please talk to the site Admin for access</>
          }
        </div>
      );
    }

    switch (activePage) {
      case 'dashboard':
        return <Dashboard onNavigate={handleNavigate} />;
      case 'contracts':
        return <Contracts isAdmin={isAdmin} searchQuery={pageOptions?.searchQuery} onNavigate={handleNavigate} />;
      case 'companies':
        return <Companies onNavigate={handleNavigate} />;
      case 'customer-detail':
        return <CustomerDetail customerName={pageOptions?.customerName} onNavigate={handleNavigate} />;
      case 'workspace':
        return <Workspace onNavigate={handleNavigate} isAdmin={isAdmin} />;
      case 'document-upload':
        return <DocumentUpload onNavigate={handleNavigate} />;
      case 'workspace-detail':
        return <WorkspaceDetail workspaceId={pageOptions?.workspaceId} onNavigate={handleNavigate} isAdmin={isAdmin} />;
      case 'new-contract':
        return <NewContract onNavigate={handleNavigate} />;
      case 'forecast-dashboard':
        if (!user?.groups?.includes('admin') && !user?.groups?.includes('finance')) {
          return (
            <div style={{ color: 'var(--bcore-text-dim)', textAlign: 'center', paddingTop: '60px', fontFamily: 'JetBrains Mono, monospace' }}>
              Access Denied &mdash; Please talk to the site Admin for access
            </div>
          );
        }
        return <ForecastDashboard onNavigate={handleNavigate} user={user} />;
      case 'growth-dashboard':
        if (!user?.groups?.includes('admin') && !user?.groups?.includes('growth')) {
          return (
            <div style={{ color: 'var(--bcore-text-dim)', textAlign: 'center', paddingTop: '60px', fontFamily: 'JetBrains Mono, monospace' }}>
              Access Denied &mdash; Please talk to the site Admin for access
            </div>
          );
        }
        return <GrowthDashboard onNavigate={handleNavigate} user={user} />;
      case 'insights':
        return <Insights />;
      case 'users':
        if (!isAdmin) {
          return (
            <div style={{ color: 'var(--bcore-text-dim)', textAlign: 'center', paddingTop: '60px', fontFamily: 'JetBrains Mono, monospace' }}>
              Access Denied &mdash; Please talk to the site Admin for access
            </div>
          );
        }
        return <Users user={user} />;
      case 'finance-import':
        if (!user?.groups?.includes('admin') && !user?.groups?.includes('finance')) {
          return (
            <div style={{ color: 'var(--bcore-text-dim)', textAlign: 'center', paddingTop: '60px', fontFamily: 'JetBrains Mono, monospace' }}>
              Access Denied &mdash; Please talk to the site Admin for access
            </div>
          );
        }
        return <FinanceImport onNavigate={handleNavigate} />;
      case 'delivery-dashboard':
        return <DeliveryDashboard onNavigate={handleNavigate} user={user} />;
      case 'delivery-config':
        return <DeliveryConfig />;
      case 'ute-reports':
        return <UteImport onNavigate={handleNavigate} />;
      case 'growth-import':
        if (!user?.groups?.includes('admin') && !user?.groups?.includes('growth') && !user?.groups?.includes('finance')) {
          return (
            <div style={{ color: 'var(--bcore-text-dim)', textAlign: 'center', paddingTop: '60px', fontFamily: 'JetBrains Mono, monospace' }}>
              Access Denied &mdash; Please talk to the site Admin for access
            </div>
          );
        }
        return <GrowthImport onNavigate={handleNavigate} />;
      case 'nda-ta-generator':
        return <NdaTaGenerator />;
      case 'agreement-review':
        if (!user?.groups?.includes('admin') && !user?.groups?.includes('growth')) {
          return (
            <div style={{ color: 'var(--bcore-text-dim)', textAlign: 'center', paddingTop: '60px', fontFamily: 'JetBrains Mono, monospace' }}>
              Access Denied &mdash; Please talk to the site Admin for access
            </div>
          );
        }
        return <AgreementReview onNavigate={handleNavigate} user={user} />;
      case 'agreement-detail':
        if (!user?.groups?.includes('admin') && !user?.groups?.includes('growth')) {
          return (
            <div style={{ color: 'var(--bcore-text-dim)', textAlign: 'center', paddingTop: '60px', fontFamily: 'JetBrains Mono, monospace' }}>
              Access Denied &mdash; Please talk to the site Admin for access
            </div>
          );
        }
        return <AgreementDetail agreementId={pageOptions?.agreementId} onNavigate={handleNavigate} user={user} />;
      case 'visit-requests':
        if (!user?.groups?.includes('admin') && !user?.groups?.includes('security')) {
          return (
            <div style={{ color: 'var(--bcore-text-dim)', textAlign: 'center', paddingTop: '60px', fontFamily: 'JetBrains Mono, monospace' }}>
              Access Denied &mdash; Please talk to the site Admin for access
            </div>
          );
        }
        return <VisitRequests onNavigate={handleNavigate} />;
      case 'clearance-requests':
        if (!user?.groups?.includes('admin') && !user?.groups?.includes('security')) {
          return (
            <div style={{ color: 'var(--bcore-text-dim)', textAlign: 'center', paddingTop: '60px', fontFamily: 'JetBrains Mono, monospace' }}>
              Access Denied &mdash; Please talk to the site Admin for access
            </div>
          );
        }
        return <ClearanceTracking onNavigate={handleNavigate} />;
      case 'settings':
        return (
          <div style={{ color: 'var(--bcore-text-dim)', textAlign: 'center', paddingTop: '60px', fontFamily: 'JetBrains Mono, monospace' }}>
            Settings &mdash; Coming Soon
          </div>
        );
      default:
        return <Dashboard />;
    }
  };

  return (
    <>
      <div className="classification-banner top">
        UNCLASSIFIED DEMO SITE
      </div>
      <div className="app-layout">
        <Sidebar
          activePage={activePage}
          onNavigate={handleNavigate}
          user={user}
          onLogout={handleLogout}
        />
        <div className="main-content">
          <Header title={PAGE_TITLES[activePage] || 'B/CORE'} />
          <div className="page-content">
            {renderPage()}
          </div>
        </div>
      </div>
      <div className="classification-banner bottom">
        UNCLASSIFIED DEMO SITE
      </div>
      {showWarning && (
        <IdleWarningModal
          remainingSeconds={remainingSeconds}
          onStayLoggedIn={dismissWarning}
          onLogout={handleLogout}
        />
      )}
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<AppContent />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
